import { Alert, ButtonLink, PrimaryButton } from '@designsforhealth/dfh-react-components'
import { Subscribe } from '@react-rxjs/core'
import React, { useContext, useReducer } from 'react'
import { IoIosCheckmarkCircleOutline } from 'react-icons/io'
import styled from 'styled-components'

import { CartFragment } from '../../../graphql/magento'
import { useResizeObserver } from '../../../hooks/useResizeObserver'
import useSiteMetadata from '../../../hooks/useSiteMetadata'
import { publishBeginCheckoutEvent } from '../../../lib/analytics/cart'
import { isMagentoScopeNotGrantedError } from '../../../lib/auth/errors'
import { useCartContext } from '../../../lib/cart'
import { ToggleCartContext } from '../../../lib/cart/context'
import { setCartFooterHeightFromBoxSize } from '../../../lib/cart/footer'
import {
  collectItemAndDesignFeePairs,
  totalQuantityExcludingDependents,
} from '../../../lib/cart/item'
import { CartStateReduceAction, reduceCartState } from '../../../lib/cart/reducers/cart-state'
import { publishConversionsBeginCheckout } from '../../../lib/conversions/actions'
import { usePageUrl } from '../../../lib/pages'
import { pluralize } from '../../../utils/textTools'
import { CartFooterSpacer } from '../../cart/CartFooterSpacer'
import CartItemContainer from '../../cart/CartItemContainer'
import { CartItemErrors } from '../../cart/CartItemErrors'
import CartPromoCode from '../../cart/CartPromoCode'
import CartSummary from '../../cart/CartSummary'
import CartSummaryToggle from '../../cart/CartSummaryToggle'
import ErrorAccessingMagentoMessage from '../../ErrorAccessingMagentoMessage'
import Link from '../Link'
import CartDetailsContainer from './CartDetailsContainer'
import CartHeader from './CartHeader'
import CartItem from './CartItem'
import CartModal from './CartModal'
import { CartUpsellFreeShipping } from './CartUpsellFreeShipping'
import LoginToViewCart from './LoginToViewCart'

import closeModalIcon from '../../../img/close_modal.svg'

const StyledCartFooter = styled.footer`
  background-color: ${({ theme }) => theme.colors.coolGray.cool150};
  border-top: 1px solid ${({ theme }) => theme.colors.coolGray.cool250};
  bottom: 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-width: 320px;
  max-width: 582px;
  min-height: 114px;
  padding: 15px;
  position: fixed;
  width: 100%;
  box-sizing: border-box;

  h5 {
    font-weight: 700;
    margin-bottom: 17px;
  }

  & > ${PrimaryButton} {
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: ${({ theme }) => theme.colors.brand.blue};
    text-transform: unset;
    font-weight: 500;
    font-size: 16px;
  }
`

const StyledItemsContainer = styled.main`
  flex: 1;
  max-height: 100%;
  overflow-y: auto;
`

const StyledCloseButton = styled(ButtonLink)`
  // provide a slightly larger touchpoint
  flex: 0 0 44px;
  height: 44px;
  margin-right: -15px;
  padding: 15px;
`

const StyledLink = styled(Link)`
  color: ${({ theme }) => theme.colors.component.textDark};
  font-size: 14px;
  padding: 10px;
`

const StyledHeaderText = styled.p`
  display: flex;
  align-items: center;
  margin: 0;
  font-size: 20px;
  font-family: Roboto, sans-serif;
  strong {
    font-size: 24px;
    font-family: Gotham, sans-serif;
    margin-right: 20px;
  }
`

const StyledButtonContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`

const CartItems: React.FC<{
  cartId: CartFragment['id']
  dispatch: React.Dispatch<CartStateReduceAction>
  items: CartFragment['items']
  shippingAddress: NonNullable<CartFragment['shipping_addresses']>[number] | undefined
}> = ({ cartId, dispatch, items, shippingAddress }) => {
  const { featureFlags } = useSiteMetadata() ?? {}

  // if label status (PL) feature flag enabled, pair items with design fee items efficiently
  if (featureFlags?.productLabelStatus) {
    const elements: React.ReactElement[] = []
    for (const pair of collectItemAndDesignFeePairs(items ?? []).values()) {
      elements.push(
        <CartItemContainer
          cartId={cartId}
          component={CartItem}
          item={pair.item}
          labelDesignFeeItem={pair.designFeeItem}
          key={pair.item.id}
          requestDispatch={dispatch}
          shippingAddress={shippingAddress}
        />,
      )
    }
    return <>{elements}</>
  }

  const elements = (items ?? []).reduce((acc: React.ReactElement[], item) => {
    if (!item) return acc
    acc.push(
      <CartItemContainer
        cartId={cartId}
        component={CartItem}
        item={item}
        key={item.id}
        requestDispatch={dispatch}
        shippingAddress={shippingAddress}
      />,
    )
    return acc
  }, [])

  return <>{elements}</>
}

const StyledCartItemErrors = styled(CartItemErrors)`
  margin-bottom: 0;
  ${Alert} {
    margin-top: 5px;
    margin-left: 5px;
    margin-right: 5px;
  }
`

const CartDetails: React.FC<{
  cartId: CartFragment['id']
  dispatch: React.Dispatch<CartStateReduceAction>
  items: CartFragment['items']
  shippingAddress: NonNullable<CartFragment['shipping_addresses']>[number] | undefined
  toggleCartModal: () => void
  totalQuantity: CartFragment['total_quantity']
  subtotalWithDiscountExclTax: NonNullable<
    NonNullable<CartFragment['prices']>['subtotal_with_discount_excluding_tax']
  >['value']
}> = React.memo(
  ({
    cartId,
    dispatch,
    items,
    shippingAddress,
    toggleCartModal,
    totalQuantity,
    subtotalWithDiscountExclTax,
  }) => (
    <>
      <CartHeader>
        <StyledHeaderText>
          <strong>Your Cart</strong> {totalQuantity}{' '}
          {pluralize({ count: totalQuantity, word: 'item' })}{' '}
          <StyledLink to="/checkout" onClick={toggleCartModal}>
            edit
          </StyledLink>
        </StyledHeaderText>
        <StyledCloseButton onClick={toggleCartModal} underline={false}>
          <img alt="Close cart modal" src={closeModalIcon} data-cy="close-cart-details-icon" />
        </StyledCloseButton>
      </CartHeader>
      <StyledItemsContainer id="cart-items-container">
        <Subscribe>
          <StyledCartItemErrors />
          {items?.length ? (
            <CartUpsellFreeShipping
              cartPriceTotal={subtotalWithDiscountExclTax}
              shippingAddress={shippingAddress}
            />
          ) : null}
          {items?.length ? (
            <CartItems
              cartId={cartId}
              dispatch={dispatch}
              items={items}
              shippingAddress={shippingAddress}
            />
          ) : (
            <h3>Your cart is empty</h3>
          )}
        </Subscribe>
      </StyledItemsContainer>
    </>
  ),
)

CartDetails.displayName = 'CartDetails'

const Cart: React.FC = () => {
  const { navigate } = usePageUrl()
  const { cart, error } = useCartContext()
  const { isCartOpen, toggleCart: toggleCartModal } = useContext(ToggleCartContext)
  const goToShipping = () => {
    if (!cart) {
      return
    }
    publishBeginCheckoutEvent(cart)
    publishConversionsBeginCheckout(cart)
    navigate('/checkout/shipping/')
  }

  const [{ codesCollapsed, formDisabled, summaryVisible }, dispatch] = useReducer(reduceCartState, {
    codesCollapsed: true,
    formDisabled: false,
    requestsPending: [],
    summaryVisible: false,
  })

  const resizeObserverRef = useResizeObserver(setCartFooterHeightFromBoxSize)

  if (isMagentoScopeNotGrantedError(error)) {
    return (
      <CartModal
        allowScroll
        isOpen={isCartOpen}
        onBackgroundClick={toggleCartModal}
        onEscapeKeydown={toggleCartModal}
      >
        <CartDetailsContainer>
          <CartHeader>
            <strong>YOUR CART:</strong>
            <ButtonLink onClick={toggleCartModal} underline={false}>
              <img alt="Close cart modal" src={closeModalIcon} />
            </ButtonLink>
          </CartHeader>
          <Alert type="error">
            <ErrorAccessingMagentoMessage />
          </Alert>
        </CartDetailsContainer>
      </CartModal>
    )
  }

  if (!isCartOpen) {
    return null
  }

  if (!cart) {
    return <LoginToViewCart toggleCartModal={toggleCartModal} isCartOpen={isCartOpen} />
  }

  const {
    id: cartId,
    items,
    shipping_addresses: shippingAddresses,
    total_quantity: totalQuantity,
    prices,
  } = cart

  const [shippingAddress] = shippingAddresses || []
  const cartTotalQuantity = totalQuantityExcludingDependents(items, totalQuantity)

  return (
    <CartModal
      allowScroll
      isOpen={isCartOpen}
      onBackgroundClick={toggleCartModal}
      onEscapeKeydown={toggleCartModal}
    >
      <CartDetailsContainer>
        <CartDetails
          cartId={cartId}
          dispatch={dispatch}
          items={items}
          shippingAddress={shippingAddress}
          toggleCartModal={toggleCartModal}
          totalQuantity={cartTotalQuantity}
          subtotalWithDiscountExclTax={prices?.subtotal_with_discount_excluding_tax?.value}
        />
        <CartFooterSpacer
          // allow for applied promo toggle in fallback
          fallbackHeight={240}
        />
      </CartDetailsContainer>
      <StyledCartFooter ref={resizeObserverRef}>
        <CartPromoCode
          cart={cart}
          codesCollapsed={codesCollapsed}
          dispatch={dispatch}
          formDisabled={formDisabled}
        />
        <CartSummary cart={cart} summaryVisible={summaryVisible} />
        <CartSummaryToggle cart={cart} dispatch={dispatch} summaryVisible={summaryVisible} />
        <PrimaryButton
          disabled={formDisabled || !items?.length}
          onClick={goToShipping}
          data-cy="minicart-secure-checkout-button"
        >
          <StyledButtonContent>
            <IoIosCheckmarkCircleOutline />
            <div>Secure Checkout</div>
          </StyledButtonContent>
        </PrimaryButton>
      </StyledCartFooter>
    </CartModal>
  )
}

export default Cart
